// Um pin BCF está dentro do volume que o corte deixa ver?
//
// Com a caixa de corte ligada, ou planos de corte soltos activos, a
// geometria fora do volume desaparece mas os pins continuavam lá:
// desenhavam-se sobre as arestas de cima da caixa, a apontar para nada.
// Aqui decide-se, para cada âncora, se fica do lado que se vê.
//
// Puro: lê os números do estado do corte (sectionSlice, só leitura) e não
// toca na caixa nem nos planos. As convenções são as de
// viewer/section-box: a caixa é a orientação (origem e três eixos
// ortonormados, no mundo three) com os limites nesse referencial local;
// a normal de um plano solto aponta para o lado que FICA (o three.js deita
// fora o que tiver distância negativa).

export type Vec3 = readonly [number, number, number]

export interface CaixaDeCorte {
  origin: Vec3
  axisX: Vec3
  axisY: Vec3
  axisZ: Vec3
  extents: { minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number }
}

export interface PlanoSolto {
  normal: Vec3
  ponto: Vec3
  activo: boolean
}

/** O corte activo: a caixa (null quando está desligada) e os planos. */
export interface Corte {
  caixa: CaixaDeCorte | null
  planos: readonly PlanoSolto[]
}

export const SEM_CORTE: Corte = { caixa: null, planos: [] }

/** Folga, em metros, para um ponto em cima de uma face contar como
 *  dentro: a âncora de um conflito numa laje cortada rente não deve
 *  piscar com o arredondamento. */
export const FOLGA_DO_CORTE_M = 0.01

const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]

/** Há corte a aplicar? */
export function haCorte(c: Corte): boolean {
  return c.caixa !== null || c.planos.some((p) => p.activo)
}

/** O ponto (mundo three) fica no volume que o corte deixa ver. */
export function dentroDoCorte(p: Vec3, c: Corte, folga: number = FOLGA_DO_CORTE_M): boolean {
  if (c.caixa) {
    const k = c.caixa
    const d: Vec3 = [p[0] - k.origin[0], p[1] - k.origin[1], p[2] - k.origin[2]]
    const lx = dot(d, k.axisX)
    const ly = dot(d, k.axisY)
    const lz = dot(d, k.axisZ)
    const e = k.extents
    if (lx < e.minX - folga || lx > e.maxX + folga) return false
    if (ly < e.minY - folga || ly > e.maxY + folga) return false
    if (lz < e.minZ - folga || lz > e.maxZ + folga) return false
  }
  for (const plano of c.planos) {
    if (!plano.activo) continue
    const d: Vec3 = [p[0] - plano.ponto[0], p[1] - plano.ponto[1], p[2] - plano.ponto[2]]
    if (dot(plano.normal, d) < -folga) return false
  }
  return true
}

/** O corte a partir do estado do viewer (sectionSlice). A caixa só conta
 *  ligada e com orientação e limites; os planos só os activos. */
export function corteDoEstado(s: {
  sectionEnabled: boolean
  sectionOrientation: { origin: Vec3; axisX: Vec3; axisY: Vec3; axisZ: Vec3 } | null
  sectionExtents: CaixaDeCorte['extents'] | null
  planosDeCorte: readonly PlanoSolto[]
}): Corte {
  const caixa =
    s.sectionEnabled && s.sectionOrientation && s.sectionExtents
      ? { ...s.sectionOrientation, extents: s.sectionExtents }
      : null
  return { caixa, planos: s.planosDeCorte.filter((p) => p.activo) }
}
